<eula-acceptance-modal>
    <!-- Native Dialog Modal -->
    <dialog id="eula-modal" class="modal" ref="dialog" aria-labelledby="eula-modal-title">
        <div class="modal-box max-w-4xl w-full max-h-[90vh] flex flex-col p-0">
                <!-- Header -->
                <div class="px-6 py-4 border-b border-base-300">
                    <h2 id="eula-modal-title" class="text-2xl font-bold text-primary">{locale?.t('eula_title')}</h2>
                    <p class="text-sm text-base-content/80 mt-1">{locale?.t('eula_subtitle')}</p>
                </div>

                <!-- EULA Content (Scrollable) -->
                <div class="flex-1 overflow-y-auto px-6 py-4" ref="eulaContent" onscroll="{handleScroll}">
                    <!-- Loading State -->
                    <div if="{state.loading}" class="flex items-center justify-center py-12">
                        <span class="loading loading-spinner loading-lg"></span>
                        <span class="ms-3 text-base-content/80">{locale?.t('eula_loading')}</span>
                    </div>

                    <!-- Error State -->
                    <div if="{state.error && !state.loading}" class="alert alert-error">
                        <i class="bi bi-exclamation-circle"></i>
                        <span>{state.error}</span>
                    </div>

                    <!-- EULA Content -->
                    <div if="{!state.loading && !state.error && state.eulaContent}">
                        <div id="eulaContentModalDisplay"></div>
                        
                        <div class="mt-6 p-4 bg-info/10 rounded-lg border border-info/20">
                            <p class="text-sm text-base-content/80">
                                <strong>{locale?.t('eula_version')}:</strong> {state.eulaVersion} | &nbsp;
                                <strong>{locale?.t('eula_effective_date')}:</strong> {tools?.formatDate(state.eulaEffectiveDate)}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Footer with Actions -->
                <div class="px-6 py-4 border-t border-base-300 bg-base-200/50">
                    <!-- Scroll indicator -->
                    <div if="{!state.hasScrolledToBottom && !state.loading}" class="alert alert-info mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span>{locale?.t('eula_scroll_to_continue')}</span>
                    </div>

                    <!-- Accept Error -->
                    <div if="{state.acceptError}" class="alert alert-error mb-4">
                        <i class="bi bi-exclamation-circle"></i>
                        <span>{state.acceptError}</span>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex gap-3 justify-end">
                        <button
                            class="btn btn-outline"
                            onclick="{handleCancel}"
                            disabled="{state.accepting}"
                        >
                            {locale?.t('cancel')}
                        </button>
                        <button
                            class="btn btn-primary"
                            onclick="{handleAccept}"
                            disabled="{!state.hasScrolledToBottom || state.accepting}"
                        >
                            <span if="{!state.accepting}">
                                <i class="bi bi-check-circle me-2"></i>
                                {locale?.t('eula_accept_button')}
                            </span>
                            <span if="{state.accepting}" class="loading loading-spinner"></span>
                        </button>
                    </div>
                </div>
        </div>
    </dialog>

    <script>
        import { getAuthService } from '../../services/service-loader';

        export default {

            state: {
                loading: false,
                error: null,
                eulaContent: '',
                eulaVersion: '',
                eulaEffectiveDate: '',
                hasScrolledToBottom: false,
                accepting: false,
                acceptError: null
            },

            onMounted() {
                // Listen for when dialog opens and auto-fetch content
                const dialog = this.refs?.dialog || document.querySelector('#eula-modal');
                if (dialog) {
                    // Use MutationObserver to detect when dialog's 'open' attribute changes
                    const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                                if (dialog.hasAttribute('open') && !this.state.eulaContent) {
                                    console.log('[EulaModal] Dialog opened, fetching content...');
                                    this.fetchEulaContent();
                                }
                            }
                        });
                    });
                    
                    observer.observe(dialog, { attributes: true });
                    console.log('[EulaModal] Mounted and listening for dialog open events');
                }
            },

            /**
             * Fetch EULA content from API
             */
            async fetchEulaContent() {
                this.state.loading = true;
                this.state.error = null;
                this.state.hasScrolledToBottom = false;
                this.state.acceptError = null;
                this.update();

                console.log('[EulaModal] Fetching Subscription Agreement from API...');
                const authService = await getAuthService();
                const result = await authService.getEula({ skipLoading: true });
                console.log('[EulaModal] Subscription Agreement fetch result:', result);
                
                if (result.success && result.eula) {
                    console.log('[EulaModal] Subscription Agreement data received');
                    this.state.eulaContent = result.eula.content;
                    this.state.eulaVersion = result.eula.version;
                    this.state.eulaEffectiveDate = result.eula.effectiveDate;
                    console.log('[EulaModal] Content length:', this.state.eulaContent?.length);
                } else {
                    console.error('[EulaModal] Failed to load Subscription Agreement:', result.error);
                    this.state.error = result.error || this.locale?.t('eula_error_load');
                }

                this.state.loading = false;
                this.update();
                
                // Set innerHTML after DOM update
                setTimeout(() => {
                    const eulaElement = document.getElementById('eulaContentModalDisplay');
                    if (eulaElement && this.state.eulaContent) {
                        eulaElement.innerHTML = this.state.eulaContent;
                    }
                }, 0);
                
                // Check if content requires scrolling
                this.$nextTick(() => {
                    this.checkIfScrollNeeded();
                });
            },

            /**
             * Show the EULA modal (for programmatic access)
             */
            async open() {
                console.log('[EulaModal] open() called');
                const modal = this.refs?.dialog || document.querySelector('#eula-modal');
                if (modal && typeof modal.showModal === 'function') {
                    modal.showModal();
                    console.log('[EulaModal] Modal opened');
                } else {
                    console.error('[EulaModal] Modal element not found');
                }
            },

            /**
             * Hide the modal
             */
            close() {
                this.state.hasScrolledToBottom = false;
                this.state.acceptError = null;
                
                const modal = this.refs?.dialog || document.querySelector('#eula-modal');
                if (modal) {
                    modal.close();
                }
                
                this.update();
            },

            /**
             * Check if the content area requires scrolling
             * If it doesn't, automatically enable the accept button
             */
            checkIfScrollNeeded() {
                const element = this.refs?.eulaContent || document.querySelector('.overflow-y-auto');
                if (!element) {
                    console.log('[EulaModal] Content element not found for scroll check');
                    return;
                }
                
                const threshold = 5; // Small threshold for floating point differences
                const needsScroll = element.scrollHeight > element.clientHeight + threshold;
                
                if (!needsScroll) {
                    console.log('[EulaModal] Content fits on screen, no scrolling needed - enabling accept button');
                    this.state.hasScrolledToBottom = true;
                    this.update();
                } else {
                    console.log('[EulaModal] Content requires scrolling - user must scroll to bottom');
                }
            },

            /**
             * Handle scroll event to detect when user reaches bottom
             */
            handleScroll(e) {
                const element = e.target;
                const threshold = 10; // Allow 10px tolerance
                const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
                
                if (isAtBottom && !this.state.hasScrolledToBottom) {
                    console.log('[EulaModal] User scrolled to bottom');
                    this.state.hasScrolledToBottom = true;
                    this.update();
                }
            },

            /**
             * Handle accept button click
             */
            async handleAccept() {
                if (!this.state.hasScrolledToBottom) {
                    return;
                }

                this.state.accepting = true;
                this.state.acceptError = null;
                this.update();

                // Accept EULA
                const authService = await getAuthService();
                const result = await authService.acceptEula(this.state.eulaVersion);

                if (result.success) {
                    console.log('[EulaModal] Subscription Agreement accepted successfully, account status:', result.accountStatus);
                    this.close();

                    // Broadcast acceptance so any page that opened the modal
                    // (e.g. login page after JWT-launch redirect) can run its
                    // own follow-up — navigation, returnTo handling, etc.
                    window.dispatchEvent(new CustomEvent('eula-accepted', {
                        detail: {
                            version: this.state.eulaVersion,
                            accountStatus: result.accountStatus
                        }
                    }));

                    // Notify parent of success (if callback provided)
                    if (this.props.onAccept) {
                        this.props.onAccept({
                            version: this.state.eulaVersion,
                            accountStatus: result.accountStatus
                        });
                    } else if (window.location.hash.includes('login')) {
                        // Login flows own post-accept navigation via their
                        // 'eula-accepted' window handlers (login.tag's JWT-launch
                        // returnto redirect, login-form's post-login destination).
                        // Reloading here would race those redirects and re-open
                        // the modal via the still-present ?eularequired=true param.
                        console.log('[EulaModal] On login route, post-accept navigation owned by login handlers');
                    } else {
                        // The page under the modal fetched its data while the EULA
                        // gate was still 403ing every API call — e.g. the workspace
                        // picker caches an empty list and shows "No workspaces
                        // available" (METALARK-883). Refreshing the cached user
                        // context can't repair that per-page state, so reboot the
                        // SPA on the current route to re-run every data load.
                        console.log('[EulaModal] Reloading so the page under the modal refetches post-acceptance');
                        window.location.reload();
                    }
                } else {
                    this.state.accepting = false;
                    this.state.acceptError = result.error || this.locale?.t('eula_error_accept');
                    this.update();
                }
            },

            /**
             * Handle cancel button click - log user out since they refused EULA
             */
            async handleCancel() {
                console.log('[EulaModal] User cancelled Subscription Agreement acceptance, logging out...');
                this.close();

                // Store reason in localStorage so it survives page reload
                localStorage.setItem('login-message', 'eula-declined');
                console.log('[EulaModal] Stored eula-declined message in localStorage');

                // Log the user out
                try {
                    const authService = await getAuthService();
                    await authService.logout();
                    console.log('[EulaModal] User logged out successfully');
                } catch (error) {
                    console.error('[EulaModal] Logout error:', error);
                }
                
                // Redirect to login page (app will handle the redirect with reason=unauthorized)
                window.location.hash = '#login';
                
                // Force page reload to ensure clean state
                setTimeout(() => {
                    window.location.reload();
                }, 100);
            },

        }
    </script>
</eula-acceptance-modal>